import * as React from 'react';
import { type JSX, useState } from 'react';
import titleTemplate from 'lib/title-template';
import Head from 'next/head';
import Page from 'components/common/page';
import { useRouter } from 'next/router';
import type { FailedSsoCode, LoginCode } from 'api/lib/auth-codes';
import Link from 'next/link';
import { useAppDispatch } from 'redux/store';
import { login } from 'redux/modules/auth-module';
import Floating from 'components/common/floating';
import Button from 'components/common/button';
import GoogleSVG from 'static/svgs/google.svg';
import AppleSVG from 'static/svgs/apple.svg';
import makeSsoUrl from 'lib/make-sso-url';
import { getSharingInvitation } from 'models/citation-list-model';

const failedSsoMessages: Record<FailedSsoCode, string> = {
  missing_email: 'An email address is required to continue.',
  unknown: 'Something went wrong.',
};
const failedLoginMessages: Record<LoginCode, string> = {
  combination_incorrect: 'That email and password combination is incorrect.',
  invalid_input: 'Please enter both email and password.',
  only_sso:
    'This account can only be logged into with Google or Apple, or by resetting the password with "Forgot password".',
};

const title =
  'font-display text-[1.75rem] leading-tight font-semibold tracking-[-0.01em] text-ink';
const label = 'mt-5 mb-1.5 block text-sm font-semibold text-ink';
const footLink = 'inline-flex min-h-11 items-center text-accent-text';

const Login = (): JSX.Element => {
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const router = useRouter();
  const queryErrorCode = router.query.error as FailedSsoCode;
  const required =
    router.query.required && 'You need to be logged in to see this page.';
  const initialMessage = failedSsoMessages[queryErrorCode] || required || null;
  const [stateMessage, setStateMessage] = useState<string | null>(null);
  const [invitationEmail, setInvitationEmail] = useState<string | null>(null);
  const [invitationListName, setInvitationListName] = useState<string | null>(
    null,
  );
  const message = stateMessage || initialMessage;
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    async function getListSharerDetails(): Promise<void> {
      if (router.query.next) {
        const shareId = Array.isArray(router.query.next)
          ? router.query.next.pop()
          : router.query.next.split('/').pop();
        if (shareId) {
          const sharerData = await getSharingInvitation(shareId);
          setInvitationEmail(sharerData.data.owner);
          setInvitationListName(sharerData.data.listName);
        }
      }
    }
    getListSharerDetails();
  }, [router.query.next]);

  const handleSubmit = async (event: React.MouseEvent): Promise<void> => {
    event.preventDefault();
    if (!(email && password)) {
      return;
    }
    try {
      await dispatch(login({ email, password })).unwrap();
      router.push((router.query.next as string) || '/');
    } catch (error) {
      setStateMessage(
        failedLoginMessages[(error as { code: LoginCode })?.code] ||
          'Something went wrong.',
      );
      return;
    }
  };
  return (
    <Page pageName="Login">
      <Head>
        <title>{titleTemplate('Log in')}</title>
        <meta
          name="description"
          content="Log into your Bibcitation account to manage your citation lists, add references and share bibliographies with others."
        />
      </Head>
      <div className="px-4">
        <Floating>
          <form>
            {!(router.query.next && invitationListName) && (
              <h1 className={title}>Log in</h1>
            )}
            {router.query.next && invitationListName && (
              <>
                <h1 className={title}>
                  Log in to join {'"' + invitationListName + '"'}
                </h1>
                <h3 className="mt-2 text-15 leading-normal text-ink-muted">
                  {invitationEmail} invited you to cooperate on the citation
                  list. Log in or create an account to continue
                </h3>
              </>
            )}
            {message !== initialMessage && (
              <div
                role="alert"
                className="mt-5 rounded-xl bg-danger-soft px-4 py-3 text-15 text-danger"
              >
                {message}
              </div>
            )}
            <label className={label} htmlFor="email">
              Email
            </label>
            <input
              type="email"
              id="email"
              onChange={(event) => setEmail(event.target.value)}
            />
            <label className={label} htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              onChange={(event) => setPassword(event.target.value)}
            />
            <div className="mt-6">
              <Button fullWidth type="brand" onClick={handleSubmit}>
                Log in
              </Button>
            </div>
            <div className="my-5 h-px bg-line" />
            <div className="flex flex-col gap-3">
              <Button
                fullWidth
                type="outline"
                href={makeSsoUrl(router.query.next, 'google', 'login')}
              >
                <GoogleSVG />
                Continue with Google
              </Button>
              <Button
                fullWidth
                type="outline"
                href={makeSsoUrl(router.query.next, 'apple', 'login')}
              >
                <AppleSVG className="fill-current" />
                Continue with Apple
              </Button>
            </div>
          </form>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-x-4 text-15">
            <Link className={footLink} href="/forgot-password">
              Forgot password?
            </Link>
            <Link
              className={footLink}
              href={`/signup?next=${encodeURIComponent(
                (router.query.next as string) || '/',
              )}`}
            >
              Sign up
            </Link>
          </div>
        </Floating>
      </div>
    </Page>
  );
};
export default Login;
